/**
 * Phase 14 — Monitoring Alert Engine API helpers + UI label/style maps.
 */
import api from "./api";

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const ingestAlert = (body) =>
  api.post("/monitoring/alerts/ingest", body).then((r) => r.data);

export const listAlerts = (params = {}) =>
  api.get("/monitoring/alerts", { params }).then((r) => r.data);

export const getAlert = (alertId) =>
  api.get(`/monitoring/alerts/${alertId}`).then((r) => r.data);

export const acknowledgeAlert = (alertId) =>
  api.patch(`/monitoring/alerts/${alertId}/acknowledge`).then((r) => r.data);

export const resolveAlert = (alertId) =>
  api.patch(`/monitoring/alerts/${alertId}/resolve`).then((r) => r.data);

export const ignoreAlert = (alertId) =>
  api.patch(`/monitoring/alerts/${alertId}/ignore`).then((r) => r.data);

// Phase 14.1 — manual SR linking from the alert-detail page.
export const linkServiceRequest = (alertId, serviceRequestId) =>
  api
    .patch(`/monitoring/alerts/${alertId}/link-service-request`, {
      service_request_id: serviceRequestId,
    })
    .then((r) => r.data);

// Mirror of `monitoring/alert_engine.SEVERITY_TO_SR_PRIORITY` so manual
// SR creation from an alert produces the same priority the rule engine
// would have stamped.
export const SEVERITY_TO_SR_PRIORITY = {
  critical: "urgent",
  high: "high",
  medium: "normal",
  low: "normal",
  info: "low",
};

export const getMonitoringStats = () =>
  api.get("/monitoring/stats").then((r) => r.data);

export const listRules = () =>
  api.get("/monitoring/rules").then((r) => r.data);

export const createRule = (body) =>
  api.post("/monitoring/rules", body).then((r) => r.data);

export const updateRule = (ruleId, body) =>
  api.patch(`/monitoring/rules/${ruleId}`, body).then((r) => r.data);

export const deleteRule = (ruleId) =>
  api.delete(`/monitoring/rules/${ruleId}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Enums + label maps
// ---------------------------------------------------------------------------

export const SEVERITIES = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "info", label: "Info" },
];

export const SEVERITY_TINT = {
  critical: "bg-red-100 text-red-900 border-red-300",
  high: "bg-orange-100 text-orange-900 border-orange-300",
  medium: "bg-amber-100 text-amber-900 border-amber-300",
  low: "bg-blue-100 text-blue-900 border-blue-300",
  info: "bg-slate-100 text-slate-700 border-slate-300",
};

export const SEVERITY_DOT = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-amber-500",
  low: "bg-blue-500",
  info: "bg-slate-400",
};

export const STATUSES = [
  { value: "new", label: "New" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "auto_ticketed", label: "Auto-ticketed" },
  { value: "resolved", label: "Resolved" },
  { value: "ignored", label: "Ignored" },
];

export const STATUS_TINT = {
  new: "bg-red-100 text-red-900 border-red-300",
  acknowledged: "bg-amber-100 text-amber-900 border-amber-300",
  auto_ticketed: "bg-blue-100 text-blue-900 border-blue-300",
  resolved: "bg-emerald-100 text-emerald-900 border-emerald-300",
  ignored: "bg-slate-100 text-slate-600 border-slate-300",
};

export const VENDORS = [
  { value: "enphase", label: "Enphase" },
  { value: "solaredge", label: "SolarEdge" },
  { value: "tesla", label: "Tesla" },
  { value: "eg4", label: "EG4" },
  { value: "manual", label: "Manual" },
  { value: "other", label: "Other" },
];

export const VENDOR_LABEL = {
  enphase: "Enphase",
  solaredge: "SolarEdge",
  tesla: "Tesla",
  eg4: "EG4",
  manual: "Manual",
  other: "Other",
};

export const ALERT_TYPES = [
  { value: "system_offline", label: "System offline" },
  { value: "production_low", label: "Production low" },
  { value: "production_zero", label: "Production zero" },
  { value: "microinverter_failure", label: "Microinverter failure" },
  { value: "optimizer_failure", label: "Optimizer failure" },
  { value: "battery_offline", label: "Battery offline" },
  { value: "battery_low_soc", label: "Battery low SOC" },
  { value: "battery_failure", label: "Battery failure" },
  { value: "gateway_offline", label: "Gateway offline" },
  { value: "grid_outage", label: "Grid outage" },
  { value: "inverter_fault", label: "Inverter fault" },
  { value: "communication_error", label: "Communication error" },
  { value: "manual_observation", label: "Manual observation" },
  { value: "other", label: "Other" },
];

export const ALERT_TYPE_LABEL = ALERT_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function relativeTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  if (ms < 0) return dt.toLocaleString();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export function isoLocal(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}
